"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, Suspense } from "react";

type AnalyticsParams = Record<string, unknown>;
type DataLayerEntry = [string, ...unknown[]];
type Gtag = (command: string, target: string | Date, params?: AnalyticsParams) => void;
type Clarity = ((command: string, key?: string, value?: string) => void) & { q?: unknown[][] };
type TrackEventOptions = { metaCustom?: boolean };
type MetaStandardEventName =
  | "CompleteRegistration"
  | "Contact"
  | "InitiateCheckout"
  | "Lead"
  | "Purchase"
  | "Search"
  | "ViewContent";
type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    dataLayer?: DataLayerEntry[];
    gtag?: Gtag;
    clarity?: Clarity;
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

// Configuration - Replace with your actual IDs
const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || "";
// Google Ads account 789-040-6258 (contact@indiacasestatus.com), created 2026-07-30.
// The old AW-18013773431 tag belongs to the paused 866-816-4643 account.
const GOOGLE_ADS_ID = "AW-18358874225";
// Signup conversion action does not exist in the new account; empty send_to
// makes trackConversion a no-op (trackGoogleAdsConversion guards on send_to).
const GOOGLE_ADS_FALLBACK_SIGNUP_SEND_TO = "";
const GOOGLE_ADS_PURCHASE_LABEL = "xecbCIGAzNgcEPHgmLJE";
const GOOGLE_ADS_FALLBACK_PURCHASE_SEND_TO = `${GOOGLE_ADS_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`;
const GOOGLE_ADS_PURCHASE_SEND_TO =
  process.env.NEXT_PUBLIC_GADS_CONVERSION_ID_PURCHASE || GOOGLE_ADS_FALLBACK_PURCHASE_SEND_TO;
const GOOGLE_ADS_INITIATED_SEND_TO =
  process.env.NEXT_PUBLIC_GADS_CONVERSION_ID_INITIATED || `${GOOGLE_ADS_ID}/2n3lCISAzNgcEPHgmLJE`;
const CLARITY_PROJECT_ID = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID || "";
const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID || "";
const GADS_PENDING_KEY = "ics_pending_gads_conversions_v1";
const GADS_EVENT_TIMEOUT_MS = 2000;

// Check if we're in production
const isProduction = process.env.NODE_ENV === "production";

type GoogleAdsConversionPayload = {
  send_to: string;
  value?: number;
  currency?: string;
  transaction_id?: string;
  plan_id?: string;
};

type PendingGoogleAdsConversion = GoogleAdsConversionPayload & {
  id: string;
  created_at: number;
};

function makeConversionId(payload: GoogleAdsConversionPayload) {
  const stableId = payload.transaction_id || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${payload.send_to}:${stableId}`;
}

function readPendingGoogleAdsConversions(): PendingGoogleAdsConversion[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(GADS_PENDING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePendingGoogleAdsConversions(items: PendingGoogleAdsConversion[]) {
  if (typeof window === "undefined") return;
  try {
    if (items.length) window.sessionStorage.setItem(GADS_PENDING_KEY, JSON.stringify(items));
    else window.sessionStorage.removeItem(GADS_PENDING_KEY);
  } catch {
    // Storage can be unavailable in strict/private contexts. The live gtag call
    // below still fires when possible.
  }
}

function persistGoogleAdsConversion(payload: GoogleAdsConversionPayload): PendingGoogleAdsConversion | null {
  if (typeof window === "undefined" || !payload.send_to) return null;
  const pending: PendingGoogleAdsConversion = {
    ...payload,
    id: makeConversionId(payload),
    created_at: Date.now(),
  };
  const existing = readPendingGoogleAdsConversions().filter((item) => item.id !== pending.id);
  writePendingGoogleAdsConversions([...existing, pending].slice(-10));
  return pending;
}

function removePendingGoogleAdsConversion(id: string) {
  const remaining = readPendingGoogleAdsConversions().filter((item) => item.id !== id);
  writePendingGoogleAdsConversions(remaining);
}

export function trackGoogleAdsConversion(
  payload: GoogleAdsConversionPayload,
  options: { persistUntilCallback?: boolean } = {},
): Promise<boolean> {
  if (!isProduction || typeof window === "undefined" || !payload.send_to) {
    return Promise.resolve(false);
  }

  const pending = options.persistUntilCallback ? persistGoogleAdsConversion(payload) : null;
  const gtag = window.gtag;
  if (!gtag) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (confirmed: boolean) => {
      if (confirmed && pending?.id) removePendingGoogleAdsConversion(pending.id);
      if (settled) return;
      settled = true;
      resolve(confirmed);
    };

    const timer = window.setTimeout(() => finish(false), GADS_EVENT_TIMEOUT_MS + 250);

    try {
      gtag("event", "conversion", {
        send_to: payload.send_to,
        value: payload.value,
        currency: payload.currency,
        transaction_id: payload.transaction_id,
        event_timeout: GADS_EVENT_TIMEOUT_MS,
        event_callback: () => {
          window.clearTimeout(timer);
          finish(true);
        },
      });
    } catch {
      window.clearTimeout(timer);
      finish(false);
    }
  });
}

function flushPendingGoogleAdsConversions() {
  if (!isProduction || typeof window === "undefined" || !window.gtag) return;
  const pending = readPendingGoogleAdsConversions();
  pending.forEach((conversion) => {
    void trackGoogleAdsConversion(conversion, { persistUntilCallback: true });
  });
}

// Google Analytics page view tracking
function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!isProduction) return;

    const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : "");

    // Send page view to GA4, and flush any paid conversions that were queued
    // before navigation.
    if (typeof window !== "undefined" && window.gtag) {
      if (GA_MEASUREMENT_ID) {
        window.gtag("config", GA_MEASUREMENT_ID, {
          page_path: url,
        });
      }
      flushPendingGoogleAdsConversions();
    }
  }, [pathname, searchParams]);
}

// Load all analytics via direct JS calls (no inline script injection — avoids minifier issues)
function useAnalyticsScripts() {
  useEffect(() => {
    if (!isProduction) return;

    const loadAnalytics = () => {
      function loadScript(src: string) {
        if (document.querySelector(`script[src="${src}"]`)) return;
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        document.head.appendChild(s);
      }

      // Google Analytics + Google Ads. Load the tag even if GA4 is missing so
      // paid-purchase conversions do not silently depend on the GA measurement ID.
      const googleTagId = GOOGLE_ADS_ID || GA_MEASUREMENT_ID;
      if (googleTagId) {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag() {
          // gtag.js only processes `arguments` objects pushed to the dataLayer;
          // plain arrays are silently ignored, so this must stay a regular
          // function using `arguments` — not rest params.
          // eslint-disable-next-line prefer-rest-params
          window.dataLayer?.push(arguments as unknown as DataLayerEntry);
        };
        window.gtag("js", new Date());
        if (GA_MEASUREMENT_ID) {
          window.gtag("config", GA_MEASUREMENT_ID, { page_path: window.location.pathname });
        }
        window.gtag("config", GOOGLE_ADS_ID);
        loadScript("https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(googleTagId));
        flushPendingGoogleAdsConversions();
      }

      // Microsoft Clarity
      if (CLARITY_PROJECT_ID) {
        window.clarity = window.clarity || (((...args: unknown[]) => {
          window.clarity!.q = window.clarity!.q || [];
          window.clarity!.q.push(args);
        }) as Clarity);
        const ct = document.createElement("script");
        ct.async = true;
        ct.src = "https://www.clarity.ms/tag/" + CLARITY_PROJECT_ID;
        const cs = document.getElementsByTagName("script")[0];
        if (cs && cs.parentNode) cs.parentNode.insertBefore(ct, cs);
        else document.head.appendChild(ct);
      }

      // Meta Pixel
      if (META_PIXEL_ID) {
        if (!window.fbq) {
          const fbq = ((...args: unknown[]) => {
            if (fbq.callMethod) fbq.callMethod(...args);
            else fbq.queue.push(args);
          }) as Fbq;
          window.fbq = fbq;
          window._fbq = fbq;
          fbq.push = fbq;
          fbq.loaded = true;
          fbq.version = "2.0";
          fbq.queue = [];
          loadScript("https://connect.facebook.net/en_US/fbevents.js");
        }
        window.fbq("init", META_PIXEL_ID);
        window.fbq("track", "PageView");
      }
    };

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(loadAnalytics, { timeout: 3000 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = globalThis.setTimeout(loadAnalytics, 2500);
    return () => globalThis.clearTimeout(timeoutId);
  }, []);
}

// Wraps useSearchParams in Suspense (required by Next.js)
function AnalyticsTracker() {
  useGoogleAnalytics();
  return null;
}

// Main Analytics component
export function Analytics() {
  useAnalyticsScripts();
  return (
    <Suspense fallback={null}>
      <AnalyticsTracker />
    </Suspense>
  );
}

// Custom event tracking helper
export function trackEvent(
  eventName: string,
  eventParams?: AnalyticsParams,
  options?: TrackEventOptions,
) {
  if (!isProduction || typeof window === "undefined") return;

  // Google Analytics
  if (window.gtag && GA_MEASUREMENT_ID) {
    window.gtag("event", eventName, eventParams);
  }

  // Microsoft Clarity custom tags
  if (window.clarity && CLARITY_PROJECT_ID) {
    window.clarity("set", eventName, JSON.stringify(eventParams));
  }

  // Meta Pixel custom events
  if (options?.metaCustom !== false && window.fbq && META_PIXEL_ID) {
    window.fbq("trackCustom", eventName, eventParams);
  }
}

export function trackMetaStandardEvent(
  eventName: MetaStandardEventName,
  eventParams?: AnalyticsParams,
  eventId?: string,
) {
  if (!isProduction || typeof window === "undefined") return;
  if (!window.fbq || !META_PIXEL_ID) return;

  if (eventId) {
    window.fbq("track", eventName, eventParams, { eventID: eventId });
    return;
  }

  window.fbq("track", eventName, eventParams);
}

// Google Ads conversion tracking
export function trackConversion(value?: number, currency?: string) {
  return trackGoogleAdsConversion({
    send_to: GOOGLE_ADS_FALLBACK_SIGNUP_SEND_TO,
    value: value ?? 1.0,
    currency: currency ?? "INR",
  });
}

// Common event helpers
export const analyticsEvents = {
  // Case tracking events
  caseAdded: (caseType: string) =>
    trackEvent("case_added", { case_type: caseType }),
  caseViewed: (caseId: string) =>
    trackEvent("case_viewed", { case_id: caseId }),

  // User events
  signUp: (method: string) => {
    trackEvent("sign_up", { method }, { metaCustom: false });
    trackMetaStandardEvent("CompleteRegistration", {
      content_name: "whatsapp_otp_signup",
      method,
      status: true,
    });
  },
  subscriptionPurchased: (
    amount: number,
    currency: string,
    transactionId?: string,
    planId?: string,
  ) => {
    const purchaseParams = {
      value: amount,
      currency,
      transaction_id: transactionId,
      items: planId ? [{ item_id: planId, item_name: planId, price: amount, quantity: 1 }] : undefined,
    };

    // GA4 ecommerce purchase (dedup key = transaction_id)
    trackEvent("purchase", purchaseParams, { metaCustom: false });
    trackMetaStandardEvent("Purchase", {
      value: amount,
      currency,
      content_ids: planId ? [planId] : undefined,
      content_type: "subscription",
      num_items: 1,
      order_id: transactionId,
      transaction_id: transactionId,
    }, transactionId);

    // Google Ads conversion — fire the paid-purchase label if configured,
    // fall back to the legacy signup label for backwards compat.
    return trackGoogleAdsConversion(
      {
        send_to: GOOGLE_ADS_PURCHASE_SEND_TO,
        value: amount,
        currency,
        transaction_id: transactionId,
        plan_id: planId,
      },
      { persistUntilCallback: true },
    );
  },
  // Fires when the user opens the Razorpay checkout modal — before payment.
  // Used as a secondary Smart Bidding signal in Google Ads (configure conversion
  // action "subscription_initiated" in Google Ads UI with send_to label below).
  subscriptionInitiated: (amount: number, currency: string, planId: string) => {
    trackEvent("subscription_initiated", { value: amount, currency, plan_id: planId }, { metaCustom: false });
    trackMetaStandardEvent("InitiateCheckout", {
      value: amount,
      currency,
      content_ids: [planId],
      content_type: "subscription",
    });
    if (!isProduction || typeof window === "undefined") return;
    if (GOOGLE_ADS_INITIATED_SEND_TO) {
      void trackGoogleAdsConversion({
        send_to: GOOGLE_ADS_INITIATED_SEND_TO,
        value: amount,
        currency,
      });
    }
  },
  login: (method: string) =>
    trackEvent("login", { method }),

  // Fires when a new signup picks their segment in the dashboard modal.
  // Drives downstream routing (advocates → /pricing) and enables segment
  // splits on every conversion event downstream.
  userSegmentChosen: (segment: "individual" | "advocate") =>
    trackEvent("user_segment_chosen", { segment }),

  // CTA clicks
  ctaClick: (ctaName: string, location: string) =>
    trackEvent("cta_click", { cta_name: ctaName, location }),

  // Blog events
  blogPostViewed: (slug: string) =>
    trackEvent("blog_post_viewed", { slug }),

  // Search events
  searchPerformed: (query: string, resultCount: number, strategy?: string) => {
    const searchParams = { search_term: query, results_count: resultCount, strategy: strategy || "unknown" };
    trackEvent("search", searchParams, { metaCustom: false });
    trackMetaStandardEvent("Search", {
      search_string: query,
      content_category: strategy || "unknown",
      results_count: resultCount,
    });
  },
  searchNoResults: (query: string) =>
    trackEvent("search_no_results", { search_term: query }),
  searchResultClicked: (query: string, caseId: string) =>
    trackEvent("search_result_clicked", { search_term: query, case_id: caseId }),

  // SEO funnel events
  seoInternalLinkClicked: (href: string, surface: string, label: string) =>
    trackEvent("seo_internal_link_clicked", { href, surface, label }, { metaCustom: false }),
  seoLandingSearchSubmitted: (mode: string, slug: string, surface: string) =>
    trackEvent("seo_landing_search_submitted", { mode, lp_slug: slug, surface }, { metaCustom: false }),

  // Error tracking
  error: (errorType: string, errorMessage: string) =>
    trackEvent("error", { error_type: errorType, error_message: errorMessage }),
};

export default Analytics;
